import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Document as ContentfulDocument } from "@contentful/rich-text-types";
import {
  ShouldRevalidateFunction,
  isRouteErrorResponse,
  useLoaderData,
  useRouteError,
  useRouteLoaderData,
} from "@remix-run/react";
import {
  HeadersFunction,
  LoaderFunction,
  MetaFunction,
  redirect,
} from "@remix-run/server-runtime";
import { Fragment } from "react";
import invariant from "tiny-invariant";
import EmbargoedWatermark from "~/components/Insight/EmbargoedWatermarking";
import FourOFour, { FourOFourCaughtData } from "~/components/shared/FourOFour";
import { generateBreadcrumbFromRequest } from "~/components/utils/breadcrumbSchema.server";
import { getBlackPage } from "~/components/utils/getBlackPage";
import { getTwicPicsURL } from "~/components/utils/getTwicPicsURL";
import SchemaOrg from "~/components/utils/SchemaOrg";
import {
  getPageSchema,
  SchemaOrgType,
} from "~/components/utils/schemaOrgUtils.server";
import { SitemapHandle } from "~/seo/sitemap.server";
import {
  createDocumentFromSection,
  getComponentFromContentfulRichTextEntry,
  getSections,
} from ".";
import type { IInsight, SpecificLocale } from "../@types/generated/contentful";
import { getEnv } from "../config";
import {
  ComponentDataProvider,
  getPageComponentData,
  PageComponentData,
} from "../contexts/ComponentDataProvider";
import { i18n } from "../i18n.server";
import { doesRouteExistOnSiteMap } from "../other-routes.server";
import {
  getAllEntriesOfType,
  getEntryBySlug,
  getPageBySlug,
} from "./index.server";

type OpenGraphType = "website" | "insight";
export const previewSearchParamKey = "preview";
const openGraphDefaultType = "website";

/**
 * Check if Contentful navigation is active using root loader data
 * @returns boolean indicating if Contentful navigation is active
 */
export function useIsContentfulNavigationActive(): boolean {
  const rootData = useRouteLoaderData("root");
  return (
    rootData?.navigation?.navigationCollection?.items[0]?.isActive ?? false
  );
}

let maxAge = 300;
if (process.env.NODE_ENV === "development") {
  maxAge = 0;
}

export const headers: HeadersFunction = () => {
  return {
    "Cache-Control": `public, s-max-age=${maxAge}`,
  };
};

export const handle = (
  content_type: string,
  baseRoute?: string,
): SitemapHandle => ({
  id: "contentful",
  getSitemapEntries: async (request: Request) => {
    /**
     *  If you have a lot of content, this may take a while.
     * You may want to limit the number of entries we fetch.
     */
    try {
      const operators: { [k: string]: string | boolean | string[] } = {};
      const select = ["fields.slug", "sys.updatedAt"];
      if (content_type === "page") {
        select.push(
          "fields.urlPathManager",
          "fields.slugPrefix",
          "fields.embargoed",
          // "fields.sitemapPriority",
          // "fields.sitemapChangeFrequency"
        );
        operators["fields.embargoed[ne]"] = true;
      }
      if (content_type === "insight") {
        select.push(
          "fields.topic",
          "fields.embargoed",
          // "fields.sitemapPriority",
          // "fields.sitemapChangeFrequency",
        );
        // https://www.contentful.com/developers/docs/javascript/tutorials/using-js-cda-sdk/
        operators["fields.embargoed[ne]"] = true;
      }

      const locale = await i18n.getLocale(request);
      const entries = (await getAllEntriesOfType({
        content_type,
        locale,
        otherParams: {
          select,
          ...operators,
        },
      })) as SpecificLocale<any>[];

      // Filter out any entries that don't have the required fields
      const validEntries = entries.filter((entry) => entry && entry.fields);

      return validEntries.map((entry) => {
        let slugPrefix =
          entry.fields.urlPathManager || entry.fields.slugPrefix || "";

        if (content_type === "insight" && entry.fields.topic?.fields?.slug) {
          slugPrefix = entry.fields.topic.fields.slug;
        }

        if (content_type === "expert") {
          slugPrefix = "experts";
        }

        // Ensure slugPrefix ends with a slash if it exists, preventing double slashes in the URL
        if (slugPrefix && !slugPrefix.endsWith("/")) {
          slugPrefix = slugPrefix + "/";
        }

        const base = `${baseRoute ? baseRoute + "/" : ""}`;
        return {
          route: `/${base}${slugPrefix}${entry.fields.slug}`,
          // changefreq: entry.fields.sitemapChangeFrequency ?? "weekly",
          changefreq: "weekly",
          lastmod: entry.sys.updatedAt,
          // priority: entry.fields.sitemapPriority ?? 0.5,
          priority: 0.5,
        };
      });
    } catch (error) {
      if (error) {
        console.error("An error occurred while fetching entries:", error);
      }

      return [];
    }
  },
});

export interface MetaInfo {
  title: string;
  description: string;
  openGraphUrl: string;
  openGraphTitle?: string;
  openGraphDescription?: string;
  openGraphType: OpenGraphType;
  openGraphImageUrl: string;
  robots?: string;
}

export const meta: MetaFunction = ({ data }: { data: { meta: MetaInfo } }) => {
  return [
    { title: data?.meta?.title },
    { name: "description", content: data?.meta?.description },
    { property: "og:title", content: data?.meta?.openGraphTitle },
    { property: "og:description", content: data?.meta?.openGraphDescription },
    { property: "og:url", content: data?.meta?.openGraphUrl },
    { property: "og:type", content: data?.meta?.openGraphType },
    { property: "og:image", content: data?.meta?.openGraphImageUrl },
    { name: "twitter:title", content: data?.meta?.openGraphTitle },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:description", content: data?.meta?.openGraphDescription },
    { name: "twitter:image", content: data?.meta?.openGraphImageUrl },
    { name: "twitter:image:src", content: data?.meta?.openGraphImageUrl },
    {
      name: "robots",
      content: data?.meta?.robots ? data.meta.robots : "index, follow",
    },
  ];
};

export function getMetaInfo({
  title,
  description,
  requestUrl,
  openGraphTitle,
  openGraphDescription,
  openGraphType,
  openGraphImageUrl,
}: {
  title: string;
  description: string;
  requestUrl: string;
  openGraphTitle?: string;
  openGraphDescription?: string;
  openGraphType?: OpenGraphType;
  openGraphImageUrl?: string;
}): MetaInfo {
  const url = new URL(requestUrl);
  const origin = getEnv("PUBLICLY_AVAILABLE_ORIGIN");
  return {
    title,
    description,
    openGraphTitle: openGraphTitle || title,
    openGraphDescription: openGraphDescription || description,
    openGraphUrl: `${origin}${url.pathname}`,
    openGraphType: openGraphType || openGraphDefaultType,
    openGraphImageUrl: openGraphImageUrl
      ? openGraphImageUrl
      : `${origin}/favicon.png`,
  };
}

export const shouldRevalidate: ShouldRevalidateFunction = ({
  defaultShouldRevalidate,
  currentUrl,
  nextUrl,
}) => {
  /**
   * Update this logic if you need the loader to re-run
   * When something other than the pathname changes (e.g. search params)
   */
  if (currentUrl.pathname === nextUrl.pathname) {
    return false;
  }
  return defaultShouldRevalidate;
};

export interface ContentfulPageLoaderData {
  componentData: PageComponentData;
  meta: MetaInfo;
  schemaOrg: SchemaOrgType;
  breadcrumbSchema: SchemaOrgType | null;
}

/**
 *
 * @param slug - The slug of the page.
 * @param prefix - Page prefix value, indicates that the page has a prefix, and the original URL should be redirected to the new URL with prefix.
 * @returns LoaderFunction
 */
export function getContentfulPageLoader(
  slug?: string,
  prefix?: string,
  options?: { include?: number },
) {
  const contentfulPageLoader: LoaderFunction = async ({
    params,
    request,
  }): Promise<ContentfulPageLoaderData | Response> => {
    // Splat routes (e.g. page.$.tsx, what-we-do.$.tsx) capture the full remaining
    // path in params["*"]; the page's slug is always its last segment.
    const splatRoute = params["*"] !== undefined ? params["*"] : "";
    const segments = splatRoute.split("/");
    const splatRoutePageSlug = segments.length
      ? segments[segments.length - 1]
      : "";

    const url = new URL(request.url);

    if (doesRouteExistOnSiteMap(url.pathname)) {
      // because this is called for every route, we'll do an early return for anything that has a other route setup. The response will be handled there.
      return new Response();
    }
    const invariantMessage =
      "expected slug to be passed as an argument or params.slug to be defined";

    // Generate the slugToQuery for querying Contentful page
    const slugToQuery =
      slug || params.slug || params.topicSlug || splatRoutePageSlug || prefix;

    const isPrefixLandingPage = Boolean(prefix) && !splatRoutePageSlug;

    invariant(slugToQuery, invariantMessage);

    const locale = await i18n.getLocale(request);
    const page = await getPageBySlug(slugToQuery, {
      locale,
      include: options?.include,
    });
    // Case 1: Page does not exist in Contentful, throwing 404
    if (!page) {
      console.log("No page found, throwing 404");
      throw new Response(
        // This is me cheating as a way to pass the color through
        JSON.stringify({
          message: "Page Not Found",
          backgroundColor: "white",
          notFound: "page",
        } as FourOFourCaughtData),
        { status: 404 },
      );
    }

    // Page exists in Contentful, enforcing canonical prefix / path rules
    const requestedPath = new URL(request.url).pathname.replace(/^\/+/, "");
    const pageSlug = page.fields.slug;
    const pageSlugPrefix =
      page.fields.urlPathManager || page.fields.slugPrefix || "";

    const expectedPath = pageSlugPrefix
      ? `${pageSlugPrefix.replace(/\/$/, "")}/${pageSlug}`
      : pageSlug;

    // Case 2: Page has a prefix, but the request does not match the expected path
    // Redirect to the expected path
    if (pageSlugPrefix && requestedPath !== expectedPath) {
      throw redirect(`/${expectedPath}`, 301);
    }

    // Case 3: Page has no prefix, but the request includes a prefix
    if (prefix && !pageSlugPrefix && !isPrefixLandingPage) {
      throw new Response(
        JSON.stringify({
          message: "Page Not Found",
          backgroundColor: "white",
          notFound: "page",
        } as FourOFourCaughtData),
        { status: 404 },
      );
    }

    const componentData = await getPageComponentData({
      page,
      locale,
      params,
      request,
    });

    const contentfulImageUrl = page.fields.openGraphImage?.fields.file.url; //TODO: this is a fallback url that will eventually be phased out

    const openGraphImageUrl = page.fields.opengraphFrontifyImage?.fields
      ? getTwicPicsURL(page.fields.opengraphFrontifyImage.fields, {
          width: 1200,
          format: "jpg",
        })
      : contentfulImageUrl
        ? `https:${contentfulImageUrl}?fm=jpg`
        : "";

    const breadcrumbSchema = generateBreadcrumbFromRequest({
      request,
      pageTitle: page.fields.title,
    });

    return {
      componentData,
      meta: getMetaInfo({
        title: page.fields.title,
        description: page.fields.description,
        requestUrl: request.url,
        openGraphTitle: page.fields.openGraphTitle || page.fields.title,
        openGraphDescription:
          page.fields.openGraphDescription || page.fields.description,
        openGraphImageUrl,
      }),
      schemaOrg: getPageSchema(page),
      breadcrumbSchema,
    };
  };
  return contentfulPageLoader;
}

export function ContentfulPageComponent() {
  const { componentData, schemaOrg, breadcrumbSchema } =
    useLoaderData<ContentfulPageLoaderData>();
  const { page } = componentData;
  const isContentfulNavActive = useIsContentfulNavigationActive();
  const colorFirstComponent =
    componentData.page?.fields?.body?.content[0]?.data?.target?.fields
      ?.colorPalette?.fields?.backgroundColor;

  const combinedSchemas = breadcrumbSchema
    ? [schemaOrg, breadcrumbSchema]
    : [schemaOrg];

  return (
    <ComponentDataProvider componentData={componentData}>
      {page?.fields.embargoed && !page?.fields.hideEmbargoedWatermark && (
        <EmbargoedWatermark />
      )}
      <SchemaOrg content={combinedSchemas} />
      {page &&
        page.fields.body &&
        getSections(page.fields.body as ContentfulDocument).map(
          (section, index) => {
            const component = documentToReactComponents(
              createDocumentFromSection(section),
              getComponentFromContentfulRichTextEntry,
            );
            if (section.type === "inset") {
              return (
                <div key={index} className="global-container">
                  {component}
                </div>
              );
            } else {
              // Spacer for the nav
              return (
                <Fragment key={JSON.stringify(section.nodes)}>
                  {index === 0 && (
                    <section
                      className="nav-color h-mobile-nav lg:h-desktop-nav"
                      style={{
                        backgroundColor:
                          isContentfulNavActive ||
                          getBlackPage(page.fields.slug)
                            ? "black"
                            : colorFirstComponent,
                      }}
                    />
                  )}
                  {component}
                </Fragment>
              );
            }
          },
        )}
    </ComponentDataProvider>
  );
}

/**
 * Combined error boundary for Remix v2.
 * Handles both route errors (404, etc.) and unexpected errors.
 * In v2, CatchBoundary was merged into ErrorBoundary.
 */
export function ContentfulErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      const data = JSON.parse(error.data) as FourOFourCaughtData;
      return (
        <FourOFour
          whatsNotFound={data.notFound}
          backgroundColor={data.backgroundColor}
        />
      );
    }
    throw new Error(`Unexpected caught response with status: ${error.status}`);
  }

  // Handle unexpected errors
  console.error(error);
  return (
    <div className="flex h-screen flex-col items-center justify-center">
      <p className="text-xl">An error occurred</p>
      {process.env.NODE_ENV !== "production" && (
        <pre className="text-sm text-gray-500">
          <code>
            {error instanceof Error ? error.message : "Unknown error"}
          </code>
        </pre>
      )}
    </div>
  );
}

// Alias for backward compatibility during migration
export const ContentfulCatchBoundary = ContentfulErrorBoundary;

export const oldSlugRedirectLoader: LoaderFunction = async ({
  params,
  request,
}) => {
  const locale = await i18n.getLocale(request);

  const invariantMessage =
    "expected slug to be passed as an argument or params.slug to be defined";
  const slugToQuery = params.slug;

  invariant(slugToQuery, invariantMessage);

  const requestUrls = request.url.split("/");
  if (
    requestUrls[requestUrls.length - 2] === "tony-blair" &&
    slugToQuery === "latest"
  ) {
    throw redirect("/experts/tony-blair");
  }

  const insight = (await getEntryBySlug("insight", slugToQuery, {
    locale,
  })) as SpecificLocale<IInsight>;

  if (!insight) {
    throw redirect(`/insights/news/${slugToQuery}`, 301);
  }

  throw redirect(
    `/insights/${insight.fields.topic.fields.slug}/${slugToQuery}`,
    301,
  );
};
